import { clearQueryCacheOnLogout } from '@/shared/api/queryClient';
import { useModalStore } from '@/shared/lib';
import type { InternalAxiosRequestConfig } from 'axios';
import { create } from 'zustand';
import { isTokenValid, logout as logoutRequest, refreshSession } from '..';
import type { Session } from './types';

const REFRESH_LOCK_NAME = 'f2x-session-refresh';
const SESSION_CHANNEL_NAME = 'f2x-session';
const CROSS_TAB_SYNC_DELAY_MS = 100;

type SessionMessage =
  | { type: 'session'; session: Session }
  | { type: 'logout' };

type SessionStatus = 'idle' | 'bootstrapping' | 'ready';

type SessionStore = {
  session: Session | null;
  status: SessionStatus;
  isRefreshing: boolean;
  isLoggingOut: boolean;
  failedQueue: Array<{
    resolve: (value: string | null) => void;
    reject: (error: unknown) => void;
  }>;
  setSession: (session: Session | null) => void;
  getAccessToken: () => string | null;
  refreshSession: (failedAccessToken?: string | null) => Promise<Session>;
  bootstrap: () => Promise<void>;
  isSessionValid: () => boolean;
  processQueue: (error: unknown, token: string | null) => void;
  handleTokenRefresh: (
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
  ) => Promise<InternalAxiosRequestConfig>;
  logout: () => Promise<void>;
  clearSession: () => Promise<void>;
};

const withoutRefreshToken = (session: Session | null): Session | null => {
  if (!session) return null;
  const rest: Session & { refreshToken?: string } = { ...session };
  delete rest.refreshToken;
  return rest;
};

const withRefreshLock = <T>(callback: () => Promise<T>): Promise<T> => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return callback();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, callback);
};

const sessionChannel =
  typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel(SESSION_CHANNEL_NAME);

const broadcastSession = (message: SessionMessage) => {
  sessionChannel?.postMessage(message);
};

const wait = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

const getBearerToken = (request: InternalAxiosRequestConfig) => {
  const header = request.headers?.Authorization;
  return typeof header === 'string' ? header.replace(/^Bearer\s+/i, '') : null;
};

const useSessionStore = create<SessionStore>()((set, get) => ({
  session: null,
  status: 'idle',
  isRefreshing: false,
  isLoggingOut: false,
  failedQueue: [],

  setSession: (session: Session | null) => {
    const nextSession = withoutRefreshToken(session);
    set({ session: nextSession });
    if (nextSession) {
      broadcastSession({ type: 'session', session: nextSession });
    }
  },

  getAccessToken: (): string | null => {
    const { session } = get();
    return session?.accessToken || null;
  },

  refreshSession: (failedAccessToken?: string | null): Promise<Session> =>
    withRefreshLock(async () => {
      if (failedAccessToken) {
        await wait(CROSS_TAB_SYNC_DELAY_MS);
        const currentSession = get().session;
        if (
          currentSession &&
          currentSession.accessToken !== failedAccessToken
        ) {
          return currentSession;
        }
      }

      const session = withoutRefreshToken(await refreshSession()) as Session;
      set({ session });
      broadcastSession({ type: 'session', session });
      return session;
    }),

  bootstrap: async () => {
    if (get().status !== 'idle') return;

    set({ status: 'bootstrapping' });

    try {
      await get().refreshSession();
    } catch {
      set({ session: null });
    } finally {
      set({ status: 'ready' });
    }
  },

  isSessionValid: (): boolean => {
    const { session } = get();

    if (!session) return false;

    const isAccessTokenValid = isTokenValid(
      session.accessExpiresTo,
      'access token'
    );
    const isRefreshTokenValid = isTokenValid(
      session.refreshExpiresTo,
      'refresh token'
    );

    return isAccessTokenValid || isRefreshTokenValid;
  },

  processQueue: (error: unknown, token: string | null = null) => {
    const { failedQueue } = get();
    failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    set({ failedQueue: [] });
  },

  handleTokenRefresh: async (
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
  ) => {
    const {
      isLoggingOut,
      isRefreshing,
      processQueue,
      failedQueue,
      refreshSession,
      logout,
    } = get();

    if (isLoggingOut) {
      return Promise.reject(new Error('Logout in progress'));
    }

    if (originalRequest.url?.includes('/token/revoke')) {
      return Promise.reject(
        new Error('Revoke request should not trigger refresh')
      );
    }

    if (originalRequest.url?.includes('/token/refresh')) {
      return Promise.reject(
        new Error('Refresh request should not trigger refresh')
      );
    }

    if (isRefreshing) {
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return originalRequest;
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;

    try {
      set({ isRefreshing: true });
      const newSession = await refreshSession(getBearerToken(originalRequest));

      processQueue(null, newSession.accessToken);

      originalRequest.headers.Authorization = `Bearer ${newSession.accessToken}`;
      return originalRequest;
    } catch (refreshError) {
      processQueue(refreshError, null);
      logout();
      return Promise.reject(refreshError);
    } finally {
      set({ isRefreshing: false });
    }
  },

  logout: async () => {
    if (get().isLoggingOut) return;

    set({ isLoggingOut: true });

    try {
      await logoutRequest();
    } catch (error) {
      console.error('Revoke token error:', error);
    } finally {
      await get().clearSession();
      broadcastSession({ type: 'logout' });
    }
  },

  clearSession: async () => {
    useModalStore.getState().closeDialog();
    useModalStore.getState().cleanup();
    await clearQueryCacheOnLogout();

    set({
      session: null,
      isRefreshing: false,
      isLoggingOut: false,
      failedQueue: [],
    });
  },
}));

sessionChannel?.addEventListener(
  'message',
  (event: MessageEvent<SessionMessage>) => {
    const message = event.data;

    if (message.type === 'session') {
      useSessionStore.setState({ session: message.session });
      return;
    }

    if (message.type === 'logout' && useSessionStore.getState().session) {
      useSessionStore.getState().clearSession();
    }
  }
);

export { useSessionStore };
